import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal, LucideIcon } from 'lucide-react';
import { IconButton } from './ButtonComponents';

export interface ActionItem {
    icon: LucideIcon;
    onClick: (e: React.MouseEvent | Event) => void;
    title?: string;
    colorClass?: string;
    show?: boolean;
    disabled?: boolean;
}

interface TableActionsProps {
    actions: ActionItem[];
    maxVisible?: number;
}

export const TableActions = ({ actions }: { actions: ActionItem[] }) => {
    // Sadece görünür olması istenen (show !== false) aksiyonları filtrele
    const visibleActions = actions.filter(a => a.show !== false);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [maxVisible, setMaxVisible] = React.useState(visibleActions.length);

    React.useEffect(() => {
        // Parent hücrenin genişliğini ölçmek için
        const parent = containerRef.current?.parentElement;
        if (!parent) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                // Sütun genişliğine göre kaç buton sığacağını hesapla
                // 1 buton ~34px yer kaplar. Container padding ~8px
                const availableWidth = entry.contentRect.width;
                const calculatedMax = Math.floor((availableWidth - 10) / 34);

                if (calculatedMax >= visibleActions.length) {
                    setMaxVisible(visibleActions.length); // Hepsi sığıyor
                } else {
                    // Sığmıyorsa en az 1 buton + 1 "Daha Fazla" (üç nokta) göster
                    setMaxVisible(Math.max(2, calculatedMax));
                }
            }
        });

        observer.observe(parent);
        return () => observer.disconnect();
    }, [visibleActions.length]);

    const showMore = visibleActions.length > maxVisible;
    const primaryActions = showMore ? visibleActions.slice(0, maxVisible - 1) : visibleActions;
    const dropdownActions = showMore ? visibleActions.slice(maxVisible - 1) : [];

    if (visibleActions.length === 0) return null;

    return (
        <div ref={containerRef} className="inline-flex items-center gap-0.5 bg-gray-50/80 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-1 shadow-sm overflow-hidden w-full max-w-max justify-center">
            {primaryActions.map((action, i) => (
                <IconButton
                    key={i}
                    icon={action.icon}
                    className={`${action.disabled ? 'opacity-30 cursor-not-allowed text-gray-400 dark:text-gray-500 hover:bg-transparent shadow-none' : (action.colorClass || 'text-gray-600') + ' hover:bg-white dark:hover:bg-gray-700'} rounded-lg`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!action.disabled) {
                            action.onClick(e);
                        }
                    }}
                    title={action.title}
                    disabled={action.disabled}
                />
            ))}
            {showMore && (
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-gray-700 text-gray-600 transition-colors flex items-center justify-center"
                            title="Daha Fazla"
                        >
                            <MoreHorizontal size={18} />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            align="end"
                            onClick={(e) => e.stopPropagation()}
                            className="z-[9999] min-w-[160px] bg-white dark:bg-card border border-borderColor rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.15)] p-1.5 animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95"
                        >
                            {dropdownActions.map((action, i) => {
                                // Extract the text color class to apply it to the text and icon
                                const textColor = action.colorClass?.split(' ').find(c => c.startsWith('text-')) || 'text-gray-700 dark:text-gray-200';

                                return (
                                    <DropdownMenu.Item
                                        key={i}
                                        onSelect={(e) => {
                                            if (action.disabled) return;
                                            e.preventDefault();
                                            action.onClick(e);
                                        }}
                                        disabled={action.disabled}
                                        className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg outline-none transition-colors ${action.disabled
                                            ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-500'
                                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer'
                                            }`}
                                    >
                                        <action.icon size={16} className={action.disabled ? 'text-gray-400 dark:text-gray-500' : textColor} />
                                        <span className={action.disabled ? 'text-gray-400 dark:text-gray-500' : textColor}>{action.title}</span>
                                    </DropdownMenu.Item>
                                );
                            })}
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            )}
        </div>
    );
};
